// Fotos „Vom Gerät“ (O91; Manuel, Sprachnachricht 26.10.2026: „von der
// Galerie Fotos einfügen“ – ANFORDERUNGEN.md H19): Fotos, die schon am Handy
// oder Tablet liegen – etwa Drohnenfotos, die per AirDrop in „Fotos“ oder
// „Dateien“ gelandet sind –, mehrere auf einmal an eine Station. Am iPhone
// heißt die Mediathek „Fotos“, nicht „Galerie“ (Gernot, 27.10.) – deshalb
// „Vom Gerät“; das deckt auch „Dateien“ ab.
//
// Jedes Foto geht denselben Weg wie eines aus der Kamera (foto.ts,
// `fotoVorbereiten`): Aufnahmezeit aus dem Foto, verkleinert, alle
// Metadaten weg – bei Drohnenfotos auch Standort und Flughöhe. Eins nach dem
// anderen, nicht alle zugleich: Drohnenfotos haben 20 Megapixel und mehr,
// der Speicher am iPad ist knapp.
//
// Was sich nicht lesen lässt (ein Video, eine kaputte Datei, am Laptop ein
// HEIC), wird übersprungen; die anderen kommen trotzdem dazu. Ist die
// Besichtigung inzwischen gesendet oder das Gerät voll, hört es auf – das
// gälte für jedes weitere Foto genauso.
//
// Seit E3c (S5; O94, Code-Prüfung O91, Befunde 1, 3, 4):
//  - Was gerade übernommen wird, steht hier, nicht nur an der Station
//    (`uebernahmeMelden`, `laufendeUebernahmen`): Verlässt der Verkäufer die
//    Station, läuft es weiter – „3 von 8“ steht dann auch in der Übersicht,
//    „Vom Gerät“ bleibt überall gesperrt, bis es fertig ist (nie zweimal
//    zugleich – der Speicher am iPad ist knapp), und „Senden“ wartet darauf.
//    Ebenso die Kamera, solange ein Foto verkleinert wird.
//  - Ein sehr großes Foto, für das dem Gerät der Speicher ausgeht, heißt
//    „zu groß für den Speicher“ – nicht „ließ sich nicht lesen“ (`sehrGross`).
//  - Die Meldung, wenn Fotos fehlen, bleibt stehen, bis man sie antippt
//    (fotos.tsx, toastZeigen mit `bleibt`).

import { jpegMasse } from "@happy-energy/kern";

/** Warum das Übernehmen aufgehört hat – null: alle durch. */
export type Abbruch = { grund: "gesendet"; meldung: string } | { grund: "platz" } | null;

export interface Uebernahme {
  uebernommen: number;
  /** Übersprungen, weil sie sich nicht lesen ließen. */
  uebersprungen: number;
  /** Übersprungen, weil dem Gerät für das sehr große Foto der Speicher ausging (seit S5, O94). */
  zuGross: number;
  /** Gar nicht mehr versucht, weil vorher abgebrochen wurde. */
  nichtVersucht: number;
  abbruch: Abbruch;
}

/** Wie ein Fehler beim Übernehmen eines Fotos zu werten ist. */
export type Fehlerart = { art: "ueberspringen" } | { art: "zu-gross" } | { art: "gesendet"; meldung: string } | { art: "platz" };

/**
 * Ab wann ein Foto „sehr groß“ ist (O94): mehr als 15 MB oder mehr als 30
 * Megapixel. Ein Drohnenfoto mit 48 Megapixeln braucht entpackt rund 200 MB –
 * daran scheitert ein iPad, ein 20-Megapixel-Foto (rund 80 MB) schafft es.
 */
export const SEHR_GROSS = { bytes: 15 * 1024 * 1024, pixel: 30_000_000 } as const;

/**
 * Ist das Foto so groß, dass ein Fehler beim Verkleinern am Speicher liegen
 * wird? Nach Dateigröße oder – ohne es zu entpacken – nach den Maßen im
 * Bildkopf (die ersten 512 KB). Was sich nicht lesen lässt: nein.
 */
export async function sehrGross(datei: Blob): Promise<boolean> {
  if (datei.size > SEHR_GROSS.bytes) return true;
  try {
    const masse = jpegMasse(new Uint8Array(await datei.slice(0, 512 * 1024).arrayBuffer()));
    return masse !== null && masse.breite * masse.hoehe > SEHR_GROSS.pixel;
  } catch {
    return false;
  }
}

// --- Was gerade übernommen wird – über die Station hinaus (O94) -------------------------------

/** Ein laufendes Übernehmen: an welcher Besichtigung und Station, wie weit; „kamera“ zählt nur fürs Warten. */
export interface LaufendeUebernahme {
  aufnahmeId: string;
  stationId: string;
  art: "vom-geraet" | "kamera";
  fertig: number;
  gesamt: number;
}

const laufend = new Map<string, LaufendeUebernahme>();
let stand: readonly LaufendeUebernahme[] = [];
const zuhoerer = new Set<() => void>();

/** Meldet ein Übernehmen (`kennung`: frei gewählt, je Lauf eine) – null: fertig. */
export function uebernahmeMelden(kennung: string, u: LaufendeUebernahme | null): void {
  if (u) laufend.set(kennung, { ...u });
  else if (!laufend.delete(kennung)) return;
  stand = [...laufend.values()];
  for (const z of zuhoerer) z();
}

/** Was gerade übernommen wird – dieselbe Liste, bis sich etwas ändert (für useSyncExternalStore). */
export function laufendeUebernahmen(): readonly LaufendeUebernahme[] {
  return stand;
}

export function uebernahmenAbonnieren(melden: () => void): () => void {
  zuhoerer.add(melden);
  return () => zuhoerer.delete(melden);
}

/** Der Fortschritt „Vom Gerät“ an dieser Besichtigung (oder Station) – zusammengezählt; null: keiner. */
export function uebernahmeStand(liste: readonly LaufendeUebernahme[], aufnahmeId: string, stationId?: string): { fertig: number; gesamt: number } | null {
  const hier = liste.filter((u) => u.art === "vom-geraet" && u.aufnahmeId === aufnahmeId && (stationId === undefined || u.stationId === stationId));
  if (hier.length === 0) return null;
  return { fertig: hier.reduce((s, u) => s + u.fertig, 0), gesamt: hier.reduce((s, u) => s + u.gesamt, 0) };
}

/**
 * Mehrere Fotos in der Reihenfolge ihrer Aufnahme – das älteste zuerst
 * (Gernot, 27.10.2026, O93: „wenn er sie antippt, … dann beginnt er
 * wahrscheinlich beim letzten. Das ist so eine Entscheidung, das will er
 * nicht entscheiden, das sollen wir entscheiden.“). Je Datei wird nur die
 * Zeit gelesen (`zeitLesen` – am Gerät die ersten 512 KB, nichts dekodiert);
 * was keine Zeit hat oder sich nicht lesen lässt, kommt in der Reihenfolge
 * der Auswahl ans Ende. Bei gleicher Zeit gilt die Auswahl.
 */
export async function nachAufnahmezeit<D>(dateien: readonly D[], zeitLesen: (datei: D) => Promise<string | null>): Promise<D[]> {
  const mitZeit: { datei: D; nr: number; zeit: number | null }[] = [];
  for (const [nr, datei] of dateien.entries()) {
    let zeit: number | null = null;
    try {
      const iso = await zeitLesen(datei);
      const t = iso === null ? Number.NaN : Date.parse(iso);
      zeit = Number.isNaN(t) ? null : t;
    } catch {
      // Unlesbar – beim Übernehmen wird es ohnehin übersprungen.
    }
    mitZeit.push({ datei, nr, zeit });
  }
  mitZeit.sort((a, b) => {
    if (a.zeit === null || b.zeit === null) return a.zeit === b.zeit ? a.nr - b.nr : a.zeit === null ? 1 : -1;
    return a.zeit - b.zeit || a.nr - b.nr;
  });
  return mitZeit.map((x) => x.datei);
}

/**
 * Übernimmt die Fotos nacheinander. `uebernehmen` macht aus einer Datei ein
 * Foto an der Station (am Gerät: vorbereiten und hinzufügen); `werten` sagt,
 * was ein Fehler bedeutet; `fortschritt` bekommt vor jedem Foto, wie viele
 * schon fertig sind.
 */
export async function fotosUebernehmen<D>(
  dateien: readonly D[],
  uebernehmen: (datei: D) => Promise<void>,
  werten: (fehler: unknown, datei: D) => Fehlerart | Promise<Fehlerart>,
  fortschritt: (fertig: number, gesamt: number) => void = () => {},
): Promise<Uebernahme> {
  let uebernommen = 0;
  let uebersprungen = 0;
  let zuGross = 0;
  for (const [i, datei] of dateien.entries()) {
    fortschritt(i, dateien.length);
    try {
      await uebernehmen(datei);
      uebernommen += 1;
    } catch (fehler) {
      const w = await werten(fehler, datei);
      if (w.art === "ueberspringen") {
        uebersprungen += 1;
        continue;
      }
      if (w.art === "zu-gross") {
        zuGross += 1;
        continue;
      }
      const nichtVersucht = dateien.length - i;
      return { uebernommen, uebersprungen, zuGross, nichtVersucht, abbruch: w.art === "gesendet" ? { grund: "gesendet", meldung: w.meldung } : { grund: "platz" } };
    }
  }
  fortschritt(dateien.length, dateien.length);
  return { uebernommen, uebersprungen, zuGross, nichtVersucht: 0, abbruch: null };
}

function fotos(n: number): string {
  return n === 1 ? "1 Foto" : `${n} Fotos`;
}

/** Was nach dem Übernehmen kurz unten steht – „fehler“, sobald eines nicht dazukam. */
export function uebernahmeMeldung(u: Uebernahme): { text: string; art: "ok" | "fehler" } {
  if (u.abbruch?.grund === "gesendet") {
    return { text: u.uebernommen > 0 ? `${fotos(u.uebernommen)} übernommen. ${u.abbruch.meldung}` : u.abbruch.meldung, art: "fehler" };
  }
  if (u.abbruch?.grund === "platz") {
    return { text: `Kein Platz mehr am Gerät – ${fotos(u.uebernommen)} übernommen, ${u.nichtVersucht} nicht. Bitte Speicher freigeben.`, art: "fehler" };
  }
  const probleme = [
    // Seit S5 (O94): sehr große Fotos, für die der Speicher nicht reichte – mit dem, was hilft.
    ...(u.zuGross > 0
      ? [`${u.zuGross === 1 ? "1 war" : `${u.zuGross} waren`} zu groß für den Speicher des Geräts – bitte andere Apps schließen und noch einmal wählen`]
      : []),
    ...(u.uebersprungen > 0 ? [`${u.uebersprungen === 1 ? "1 ließ" : `${u.uebersprungen} ließen`} sich nicht lesen (kein Foto oder beschädigt)`] : []),
  ];
  if (probleme.length === 0) return { text: `${fotos(u.uebernommen)} übernommen`, art: "ok" };
  return { text: `${u.uebernommen === 0 ? "Kein Foto" : fotos(u.uebernommen)} übernommen – ${probleme.join("; ")}.`, art: "fehler" };
}

/** Der Hinweis, solange übernommen wird: „Fotos werden übernommen … 3 von 8“. */
export function fortschrittText(fertig: number, gesamt: number): string {
  return gesamt === 1 ? "Foto wird übernommen …" : `Fotos werden übernommen … ${Math.min(fertig + 1, gesamt)} von ${gesamt}`;
}
